// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { Dictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { dataViewNotFound, getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { Draft } from "immer";
import { FeaturedBrandsModule, FeaturedBrandsParameter } from "@vanmeter/XDL/types";

export type FeaturedBrandsDataView = {
    value?: FeaturedBrandsModule[];
    isLoading: boolean;
};

export type FeaturedBrandsState = {
    dataViews: Dictionary<FeaturedBrandsDataView>;
};

const initialFeaturedBrandsState: FeaturedBrandsState = { dataViews: {} };

const xFeaturedBrands = createTypedReducerWithImmer(initialFeaturedBrandsState, {
    "xFeaturedBrands/StartLoading": (draft: Draft<FeaturedBrandsState>, action: { parameter: any }) => {
        const key = getDataViewKey(action.parameter);
        draft.dataViews[key] = { value: undefined, isLoading: true };
    },
    "xFeaturedBrands/CompleteLoading": (
        draft: Draft<FeaturedBrandsState>,
        action: { parameter: FeaturedBrandsParameter; featuredBrands: FeaturedBrandsModule[] },
    ) => {
        const key = getDataViewKey(action.parameter);
        draft.dataViews[key] = {
            value: action.featuredBrands,
            isLoading: false,
        };
    },
});

(reducers as any).xFeaturedBrands = xFeaturedBrands;

export type xFeaturedBrandsState = {
    xFeaturedBrands: FeaturedBrandsState;
} & ApplicationState;

export function getFeaturedBrandsDataView(state: xFeaturedBrandsState, parameter: any): FeaturedBrandsDataView {
    const key = getDataViewKey(parameter);
    const dataView = state.xFeaturedBrands?.dataViews?.[key];
    if (dataView) {
        return dataView;
    }
    return dataViewNotFound as FeaturedBrandsDataView;
}

type xFeaturedBrandsReducers = {
    tabs: typeof xFeaturedBrands;
};

export type xFeaturedBrandsActions = Parameters<xFeaturedBrandsReducers[keyof xFeaturedBrandsReducers]>[1] | AnyAction;
